import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StoreService } from '../Services/store.service';
import { UserService } from '../Services/user.service';

@Component({
  selector: 'app-stores',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './stores.component.html',
  styleUrls: ['./stores.component.css'],
  providers: [StoreService, UserService],
})
export class StoresComponent implements OnInit {
  stores: any;
  storeSearchTerm: string = '';
  storeName: string = '';
  storeAddress: string = '';
  storeContact: string = '';
  storeIpAddress: string = '';
  storeEmail: string = '';
  selectedStoreId: number | null = null;
  name: string = '';
  email: string = '';
  ipAddress: string = '';
  selectedRole: string = '';
  token: string = '';
  username: string = '';
  password: string = '';
  isToken: boolean = false;
  disabled: boolean = false;
  loading: boolean = true;
  toastMessage = '';
  toastType = '';

  storeAdmins: any[] = [];
  selectedAdminStoreId: number | null = null;
  isLoadingAdmins = false;

  pageNumber: number = 1;
  pageSize: number = 15;
  paginatedStores: any[] = [];

  constructor(
    private _storeService: StoreService,
    private _userservice: UserService,
  ) {}

  ngOnInit(): void {
    this.getAllStores();
  }

  showToast(message: string, type: 'success' | 'error' = 'success') {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => { this.toastMessage = ''; }, 3000);
  }

  getAllStores() {
    this.loading = true;
    this._storeService.getStores().subscribe((response: any) => {
      this.stores = response.result;
      this.loading = false;
      this.updatePaginatedStores();
    });
  }

  filteredStores() {
    if (!this.stores) return [];
    const term = this.storeSearchTerm.toLowerCase();
    return this.stores.filter((store: any) =>
      store.name?.toLowerCase().includes(term) || store.email?.toLowerCase().includes(term)
    );
  }

  addStore(): void {
    const storeInfo = {
      name: this.storeName,
      email: this.storeEmail,
      address: this.storeAddress,
      contact: this.storeContact,
      ipAddress: this.storeIpAddress,
    };

    this._storeService.addNewStore(storeInfo).subscribe({
      next: () => {
        this.getAllStores();
        this.showToast('Store added successfully');
        const modalElement = document.getElementById('staticBackdrop');
        if (modalElement) {
          const modalInstance =
            (window as any).bootstrap.Modal.getInstance(modalElement) ||
            new (window as any).bootstrap.Modal(modalElement);
          modalInstance.hide();
        }
        this.cleanUpModalBackdrop();
      },
      error: (err) => {
        console.error('Error adding store:', err);
        this.showToast('Error adding store', 'error');
      },
    });
  }

  cleanUpModalBackdrop(): void {
    document.body.classList.remove('modal-open');
    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }
  }

  openAddUserModal(storeId: number): void {
    this.selectedStoreId = storeId;
    const modalElement = document.getElementById('addUserModal');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  addUser(): void {
    if (!this.selectedStoreId) {
      this.showToast('No store selected to add user.', 'error');
      return;
    }
    const userInfo = {
      name: this.name,
      email: this.email,
      ipAddress: this.ipAddress,
      storeId: this.selectedStoreId,
      roleToAssign: this.selectedRole,
    };
    this._userservice.addNewUser(userInfo).subscribe({
      next: (res: any) => {
        this.token = res.apiKey;
        this.username = res.user;
        this.password = res.password;
        this.isToken = true;
      },
      error: (err) => {
        console.error('Error adding user:', err);
        this.showToast('Error adding user', 'error');
      },
    });
    this.disabled = true;
  }

  deleteStore(storeId: number): void {
    if (!confirm('Deactivate this store? This will disable all associated logins.')) return;
    this._storeService.deleteStore(storeId).subscribe({
      next: () => {
        this.showToast('Store deactivated');
        this.getAllStores();
      },
      error: (err) => {
        console.error('Error deleting store:', err);
        this.showToast('Failed to deactivate store', 'error');
      },
    });
  }

  copyToken(): void {
    let combinedText = '';
    if (this.selectedRole === 'StoreUser') {
      combinedText = `Username: ${this.username}\nPassword: ${this.password}`;
    } else if (this.selectedRole === 'StoreAdmin') {
      combinedText = `Token: ${this.token}\nUsername: ${this.username}\nPassword: ${this.password}`;
    }
    navigator.clipboard.writeText(combinedText).then(
      () => this.showToast('Copied to clipboard'),
      () => this.showToast('Copy failed', 'error'),
    );
  }

  onModalClose(): void {
    this.storeName = '';
    this.storeEmail = '';
    this.storeIpAddress = '';
    this.storeAddress = '';
    this.storeContact = '';
    this.name = '';
    this.email = '';
    this.ipAddress = '';
    this.token = '';
    this.username = '';
    this.password = '';
    this.isToken = false;
    this.disabled = false;
    this.selectedRole = '';
    this.selectedStoreId = null;
  }

  openStoreAdmins(storeId: number): void {
    this.selectedAdminStoreId = storeId;
    this.storeAdmins = [];
    this.isLoadingAdmins = true;

    const modalElement = document.getElementById('storeAdminsModal');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }

    this._storeService.getStoreAdmins(storeId).subscribe({
      next: (res: any) => {
        this.storeAdmins = res.result || [];
        this.isLoadingAdmins = false;
      },
      error: () => {
        this.isLoadingAdmins = false;
        this.showToast('Unable to load store admins', 'error');
      },
    });
  }

  copyAdminToken(token: string): void {
    navigator.clipboard.writeText(token).then(
      () => this.showToast('API key copied'),
      () => this.showToast('Copy failed', 'error'),
    );
  }

  maskApiKey(token: string): string {
    if (!token || token.length < 8) return '***********';
    return `${token.substring(0, 3)}****${token.substring(token.length - 3)}`;
  }

  activateStore(storeId: number): void {
    if (!confirm('Activate this store?')) return;
    this._storeService.activateStore(storeId).subscribe({
      next: () => {
        this.showToast('Store activated');
        this.getAllStores();
      },
      error: () => this.showToast('Unable to activate store', 'error'),
    });
  }

  onSearchChange(): void {
    this.pageNumber = 1;
    this.updatePaginatedStores();
  }

  updatePaginatedStores(): void {
    const startIndex = (this.pageNumber - 1) * this.pageSize;
    this.paginatedStores = this.filteredStores().slice(startIndex, startIndex + this.pageSize);
  }

  totalPages(): number {
    return Math.ceil(this.filteredStores().length / this.pageSize);
  }

  visiblePages(): number[] {
    const total = this.totalPages();
    const current = this.pageNumber;
    let start = Math.max(current - 1, 1);
    let end = Math.min(start + 2, total);
    if (end - start < 2) start = Math.max(end - 2, 1);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  goToPage(page: number): void {
    if (page !== this.pageNumber) { this.pageNumber = page; this.updatePaginatedStores(); }
  }

  nextPage(): void {
    if (this.pageNumber < this.totalPages()) { this.pageNumber++; this.updatePaginatedStores(); }
  }

  previousPage(): void {
    if (this.pageNumber > 1) { this.pageNumber--; this.updatePaginatedStores(); }
  }
}
